import React, { useEffect, useMemo, useState } from 'react';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { Row, Column } from '../../../../global/components/row';
import { size, get, set, first, sortBy } from '../../../../global/lib/lodash';
import { Text } from '../../../../global/components/text';
import { FlexGridRow, MarginLeftZebraList, ZebraList } from 'global/components/flex-grid-list';
import { RealmAssignmentsContainer } from './style';
import {
  AssignmentDocumentRow,
  applyDivisionLocationAccessFromSourceToTarget,
  isDocumentItemTaggedForRealm,
  uniformHeaderRoomsAndBulkCompare,
} from '../assignment-list/AssignmentRows';
import { settingsCopiedSnackAlert } from '../assignment-list/assignment-copy-snacks';
import { AssignmentLocationRow } from '../assignment-list/AssignmentLocationRow';
import {
  ASSIGNMENT_FORM_TAB,
  AssignmentFormFooter,
  AssignmentFormTabPanel,
  AssignmentFormTabs,
  AssignmentsModalFrame,
  AssignmentPreviewDialogFrame,
} from '../assignment-list/assignment-form-shell';
import { RoomSelect } from '../../../../global/components/room-select';
import { CenterLoader, Loader } from '../../../../global/styles';
import SnackAlerts from '../../../../global/components/snack-alerts';
import sendData from '../../../../redux/actions/sendData';
import { useWindowSize } from '../../../../global/hooks/useWindowSize';
import { EDITIONINCLUDES } from '../manageedition';
import { useUser } from '../../../../global/hooks/useUser';

const Doc = (props) => {
  const { updateChecks, copiedRooms, onCopy, isMobileXS, rooms, doc } = props;

  const [headerRealms, bulkCompareRooms] = useMemo(
    () => uniformHeaderRoomsAndBulkCompare(doc?.items, (it) => it.rooms),
    [doc]
  );

  const fixMargins = { marginLeft: 5, pointerEvents: 'none' };

  const title = (style = { minWidth: 200 }) => {
    return (
      <Row style={style}>
        <Text type="title_medium" bold>{`${get(doc, 'document.classification.system.name', get(doc, 'classification.name', ''))} (${size(doc.items)}):`}</Text>
      </Row>
    );
  };

  const CONT = isMobileXS ? Column : Row;

  return (
    <div style={{ marginTop: 10 }}>
      <CONT>
        {title()}
        <div style={{ marginLeft: 'auto' }}>
          <Row style={{ alignItems: 'center' }}>
            <RoomSelect
              value={headerRealms}
              rooms={rooms}
              onCopy={onCopy}
              onPaste={copiedRooms ? () => copiedRooms : undefined}
              onChange={(s, areEqual) => updateChecks(doc, s, null, areEqual)}
              type="inlineModal"
              modalTitle={title({ ...fixMargins, marginLeft: 30, marginTop: 4 })}
            />
          </Row>
        </div>
      </CONT>

      <MarginLeftZebraList>
        {doc.items.map((item, _i) => (
          <FlexGridRow data-testid={`checklist-${_i}`} key={item.id ?? _i}>
            <AssignmentDocumentRow
              item={item}
              doc={doc}
              rooms={rooms}
              updateChecks={updateChecks}
              onCopy={onCopy}
              copiedRooms={copiedRooms}
              isMobileXS={isMobileXS}
              fixMargins={fixMargins}
              bulkCompareRooms={bulkCompareRooms}
            />
          </FlexGridRow>
        ))}
      </MarginLeftZebraList>
    </div>
  );
};

export const RealmAssignments = (props) => {
  const { editionId } = props;
  const [realms, setRealms] = useState([]);
  const [state, setState] = useState({
    copiedRooms: null,
    copiedLocationAccess: null,
    loading: false,
    edDocs: [],
  });
  const [alerts, setAlerts] = useState();
  const [preview, setPreview] = useState({
    open: false,
    realm: null,
    title: '',
    onClose: () => {
      setPreview((p) => ({ ...p, open: false }));
    },
  });
  const [previewShowOnlyChecked, setPreviewShowOnlyChecked] = useState(false);
  const [mainTab, setMainTab] = useState(ASSIGNMENT_FORM_TAB.location);
  const [docs, setDocs] = useState([]);

  useEffect(() => {
    setState((s) => ({
      ...s,
      copiedRooms: null,
      copiedLocationAccess: null,
    }));
  }, [mainTab]);

  const { userBuilderRealm } = useUser();

  const { isMobileXS } = useWindowSize();

  const edition = useRetrieveData(
    { url: `/editions/${editionId}` },
    {
      build: ['editions'],
      params: {
        include: EDITIONINCLUDES.join(','),
      },
      transformData: (t) => {
        return t.find((tt) => tt.id == editionId);
      },
    }
  );

  const edDocs = useRetrieveData(
    { url: `/edition-documents` },
    {
      build: ['editionDocuments'],
      params: {
        filter: {
          edition_id: editionId,
        },
        include: 'classification,document,superset,classification.system',
      },
    }
  );

  const roomData = useRetrieveData(
    { url: '/realms' },
    {
      build: ['realms'],
      params: {
        filter: {
          tier: 'division',
          parent_id: userBuilderRealm.id,
        },
        include: 'parent',
      },
    }
  );

  useEffect(() => {
    if (!edDocs.loading && !edition.loading) {
      let edocs;

      const classes = {};

      edDocs.data.forEach((ed) => {
        const classificationId = get(ed, 'classification.system.id');

        set(ed, 'document.classification', ed.classification);

        const docsCfg = get(edition, 'data.config.docs', {}) || get(edition, 'config.docs', {});
        const entry = docsCfg[ed.id] ?? docsCfg[String(ed.id)] ?? docsCfg[Number(ed.id)];
        let rawRealms = get(entry, 'realms');
        if (rawRealms === undefined) {
          rawRealms = get(edition, `data.config.docs.${ed.id}.realms`);
        }
        if (rawRealms === undefined) {
          rawRealms = get(edition, `data.config.docs.${String(ed.id)}.realms`);
        }
        ed.rooms = Array.isArray(rawRealms) ? rawRealms : [];

        set(classes, classificationId, sortBy([...get(classes, classificationId, []), ed], 'order'));
      });

      edocs = sortBy(
        Object.keys(classes).map((cl) => ({ ...first(classes[cl]), items: classes[cl] })),
        'order'
      );

      setDocs(edocs);
    }
  }, [edDocs.loading, edition.loading]);

  useEffect(() => {
    if (!roomData.loading) {
      const rms = roomData.data.map((r) => ({ ...r, groupBy: get(r, 'parent.name') }));
      setRealms(rms);
    }
  }, [roomData.loading]);

  useEffect(() => {
    if (!preview.open) {
      setPreviewShowOnlyChecked(false);
    }
  }, [preview.open]);

  const onChange = () => {
    props.onChange && props.onChange();
  };

  const updateChecks = (d, sel, item, areEqual) => {
    if (!item) {
      d.rooms = sel;
    }
    setDocs((clss) => {
      return clss.map((doc) => {
        if (doc.id == d.id) {
          doc = {
            ...doc,
            items: doc.items.map((c) => {
              const setRooms = () => {
                c.rooms = sel;
              };

              if (item) {
                if (c.id == item.id) {
                  setRooms();
                }
              } else {
                setRooms();
              }

              return c;
            }),
          };
        }
        return doc;
      });
    });

    if (!areEqual) {
      onChange();
    }
  };

  const onCopy = (rms) => {
    setState((s) => ({ ...s, copiedRooms: rms }));
    setAlerts(settingsCopiedSnackAlert());
  };

  const onCopyLocationAccess = (realm) => {
    setState((s) => ({ ...s, copiedLocationAccess: { id: realm.id, name: realm.name } }));
    setAlerts(settingsCopiedSnackAlert());
  };

  const onPasteLocationAccess = (targetRealm) => {
    const source = state.copiedLocationAccess;
    if (!source || String(source.id) === String(targetRealm.id)) {
      return;
    }
    const sourceRef = { id: source.id, name: source.name };
    const targetRef = { id: targetRealm.id, name: targetRealm.name };
    setDocs((prev) => applyDivisionLocationAccessFromSourceToTarget(prev, sourceRef, targetRef, realms));
    onChange();
  };

  const cancel = () => {
    props.onCancel && props.onCancel();
  };

  const previewClick = (realm) => {
    setPreview((p) => ({
      ...p,
      realm,
      open: true,
      title: `${realm.name} — Edit division access`,
    }));
  };

  const renderRealmsList = () => {
    return (
      <>
        {edition.loading && <Loader />}
        {!edition.loading && (
          <ZebraList>
            {realms.map((realm) => {
              let count = 0;
              docs.forEach((doc) => {
                doc.items.forEach((item) => {
                  if (isDocumentItemTaggedForRealm(item, realm, realms)) {
                    count += 1;
                  }
                });
              });
              return (
                <AssignmentLocationRow
                  key={realm.id ?? realm.name}
                  locationId={realm.id}
                  name={realm.name}
                  countLabel={`${count} Documents`}
                  copiedLocationAccess={state.copiedLocationAccess}
                  onPreview={() => previewClick(realm)}
                  onCopyLocationAccess={() => onCopyLocationAccess(realm)}
                  onPasteLocationAccess={() => onPasteLocationAccess(realm)}
                  titlePreview="Edit which documents this division can access"
                  titleCopy="Copy document access for this division"
                  titlePaste="Paste copied division access"
                />
              );
            })}
          </ZebraList>
        )}
      </>
    );
  };

  const submit = async () => {
    try {
      setState((s) => ({ ...s, loading: true }));

      const config = {
        ...get(edition, 'data.config', {}),
        docs: {},
      };

      Object.keys(docs).map((k) => {
        const doc = docs[k];
        doc.items.map((item) => {
          if (size(item.rooms)) {
            item.rooms.map((r) => {
              const key = `docs.${item.id}`;
              set(config, `${key}.realms`, [...get(config, `${key}.realms`, []), { id: r.id, name: r.name }]);
              set(config, `${key}.documentableId`, item.documentableId);
              set(config, `${key}.documentableType`, item.documentableType);
            });
          }
        });
      });

      const data = {
        data: {
          id: editionId,
          type: 'editions',
          attributes: {
            config,
          },
        },
      };

      await sendData(
        {
          url: `/editions/${editionId}`,
          method: 'patch',
          data,
        },
        { simulate: false }
      );

      props.onClose && props.onClose(true);
    } catch (err) {
      console.warn(err);
      setAlerts({ message: err.message, severity: 'error' });
    } finally {
      setState((s) => ({ ...s, loading: false }));
    }
  };

  const handleSubmit = () => {
    props.onSubmit?.(docs, edition) ?? submit();
  };

  return (
    <RealmAssignmentsContainer style={{ paddingBottom: 72 }}>
      <AssignmentFormTabs
        title="Location Assignments"
        subtitleAdd={get(props, 'subtitleAdd', '')}
        subtitle="Location assignments hide the selected content from all other divisions."
        value={mainTab}
        onChange={setMainTab}
      />

      {mainTab === ASSIGNMENT_FORM_TAB.location && (
        <AssignmentFormTabPanel
          title="Division Content"
          description="Below is a list of divisions and which content will be visible to them."
        >
          {renderRealmsList()}
        </AssignmentFormTabPanel>
      )}

      {mainTab === ASSIGNMENT_FORM_TAB.criteria && (
        <AssignmentFormTabPanel title="Document Location Assignments">
          {edDocs.loading ? (
            <CenterLoader />
          ) : (
            !preview.open &&
            docs.map((doc, i) => <Doc key={`${i}`} doc={doc} updateChecks={updateChecks} onCopy={onCopy} isMobileXS={isMobileXS} rooms={realms} {...state} />)
          )}
        </AssignmentFormTabPanel>
      )}

      <AssignmentFormFooter
        onCancel={cancel}
        onSave={handleSubmit}
        saving={state.loading}
        cancelButtonTitle="Cancel"
        saveButtonTitle="Save"
      />

      <SnackAlerts alerts={alerts} setAlerts={setAlerts} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} />

      <AssignmentPreviewDialogFrame
        open={preview.open}
        onClose={preview.onClose}
        title={preview.title}
        filter={
          preview.realm
            ? {
                active: previewShowOnlyChecked,
                onToggle: () => setPreviewShowOnlyChecked((v) => !v),
                titleShowAll: 'Show all documents',
                titleShowOnly: 'Show only assigned documents',
              }
            : undefined
        }
      >
        {docs.map((doc, di) => {
          const visibleItems =
            previewShowOnlyChecked && preview.realm
              ? doc.items.filter((item) => isDocumentItemTaggedForRealm(item, preview.realm, realms))
              : doc.items;
          if (previewShowOnlyChecked && preview.realm && !size(visibleItems)) {
            return null;
          }
          return (
            <div key={doc.id ?? di} style={{ marginBottom: 24 }}>
              <Text type="title_medium" bold>
                {`${get(doc, 'items.0.document.classification.system.name', get(doc, 'items.0.classification.name', ''))} (${size(visibleItems)}${previewShowOnlyChecked && size(visibleItems) !== size(doc.items) ? ` of ${size(doc.items)}` : ''}):`}
              </Text>
              <MarginLeftZebraList>
                {visibleItems.map((item, ii) => (
                  <FlexGridRow data-testid={`preview-checklist-${di}-${ii}`} key={item.id ?? ii}>
                    <AssignmentDocumentRow
                      item={item}
                      doc={doc}
                      updateChecks={updateChecks}
                      interaction="previewCheckbox"
                      previewRealm={preview.realm}
                      allRealms={realms}
                      isMobileXS={isMobileXS}
                    />
                  </FlexGridRow>
                ))}
              </MarginLeftZebraList>
            </div>
          );
        })}
        {previewShowOnlyChecked &&
          preview.realm &&
          size(docs) > 0 &&
          !docs.some((d) => d.items.some((it) => isDocumentItemTaggedForRealm(it, preview.realm, realms))) && (
            <Text type="label_large" style={{ marginTop: 16 }}>
              No documents are assigned to this division.
            </Text>
          )}
        {!size(docs) && (
          <Text type="label_large" style={{ marginTop: 16 }}>
            No documents loaded.
          </Text>
        )}
      </AssignmentPreviewDialogFrame>
    </RealmAssignmentsContainer>
  );
};

export const RealmAssignmentsModal = (props) => (
  <AssignmentsModalFrame modalProps={props.modalProps}>{(handlers) => <RealmAssignments {...props} {...handlers} />}</AssignmentsModalFrame>
);
